"use client";

import { useGSAP } from "@gsap/react";
import { SplitText } from "gsap/all";
import gsap from "gsap";
import { useTransition } from "@/context/TransitionContext";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const NutritionSection = () => {
  const { triggerTransition } = useTransition();
  const sectionRef = useRef<HTMLElement>(null);
  const caterersVideoRef = useRef<HTMLVideoElement>(null);
  const restaurantsVideoRef = useRef<HTMLVideoElement>(null);

  const refreshScrollTriggers = () => {
    window.requestAnimationFrame(() => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh(true);
    });
  };

  useGSAP(() => {
    const titleSplit = SplitText.create(".nutrition-title", {
      type: "words, chars",
    });
    const paragraphSplit = SplitText.create(".nutrition-section p", {
      type: "words, lines",
      linesClass: "paragraph-line",
    });

    const mm = gsap.matchMedia();

    mm.add(
      {
        isMobile: "(max-width: 768px)",
        isDesktop: "(min-width: 769px)",
      },
      (context) => {
        const { isMobile } = context.conditions as {
          isMobile: boolean;
        };

        const contentTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".nutrition-section",
            start: isMobile ? "top 75%" : "top center",
          },
        });
        contentTl
          .from(titleSplit.chars, {
            yPercent: 100,
            stagger: 0.02,
            ease: "power2.out",
          })
          .from(paragraphSplit.words, {
            yPercent: 300,
            rotate: 3,
            ease: "power1.inOut",
            duration: 0.8,
            stagger: 0.01,
          }, "<0.1");

        if (!isMobile) {
          contentTl
            .from(
              caterersVideoRef.current,
              {
                x: -80,
                opacity: 0,
                duration: 0.8,
                ease: "power3.out",
              },
              "<0.2"
            )
            .from(
              restaurantsVideoRef.current,
              {
                x: 80,
                opacity: 0,
                duration: 0.8,
                ease: "power3.out",
              },
              "+=0.15"
            );
        }


      }
    );

    return () => {
      mm.revert();
      titleSplit.revert();
      paragraphSplit.revert();
    };
  });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const videos = [caterersVideoRef.current, restaurantsVideoRef.current].filter(
      (video): video is HTMLVideoElement => Boolean(video)
    );
    if (videos.length === 0) return;

    const playAll = () => {
      videos.forEach((video) => {
        video.play().catch(() => {
          // Ignore autoplay rejections.
        });
      });
    };

    const warmUpVideos = () => {
      videos.forEach((video) => {
        if (video.preload !== "auto") {
          video.preload = "auto";
        }
        if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
          video.load();
        }
      });
    };

    const pauseAll = () => {
      videos.forEach((video) => {
        video.pause();
      });
    };

    const warmupObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        warmUpVideos();
        warmupObserver.disconnect();
      },
      { rootMargin: "450px 0px", threshold: 0 }
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        if (entry.isIntersecting) {
          playAll();
          return;
        }
        pauseAll();
      },
      { threshold: 0.2 }
    );

    warmupObserver.observe(section);
    observer.observe(section);

    return () => {
      warmupObserver.disconnect();
      observer.disconnect();
      pauseAll();
    };
  }, []);

  return (

    <section ref={sectionRef} className="nutrition-section bg-[#E6DAC6] relative overflow-hidden min-h-dvh flex flex-col justify-center">
      <Image
        src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472251/slider-dip_naoon1.png"
        alt="Decoration"
        fill
        className="object-cover opacity-60 pointer-events-none"
        sizes="100vw"
      />

      <div className="container mx-auto px-5 relative z-10 w-full h-full py-20 md:py-24 space-y-16 md:space-y-20">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-24">
          <div className="nutrition-title-wrapper overflow-visible flex flex-col items-start gap-4 border-l-2 border-[#523122]/25 pl-4">
            <h1 className="nutrition-title text-6xl md:text-7xl font-bold uppercase text-[#523122] leading-[0.9] tracking-tighter">
              FOR CATERERS
            </h1>
            <p className="font-paragraph text-2xl md:text-3xl text-dark-brown leading-tight max-w-xl">
              We innovate your menu for less than the price of{" "}
              <span className="text-[#523122] font-bold">chai.</span>
            </p>
            <button onClick={() => triggerTransition("/caterers")} className="nutrition-button mt-4">
              Know More
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>

          <video
            ref={caterersVideoRef}
            src="https://res.cloudinary.com/ddahd2kco/video/upload/v1770803734/WhatsApp_Video_2026-02-10_at_19.54.39_i1q8iu.mp4"
            loop
            muted
            playsInline
            preload="metadata"
            poster="/caterers_sideimage.png"
            onLoadedMetadata={refreshScrollTriggers}
            aria-hidden="true"
            tabIndex={-1}
            className="w-full max-w-[420px] aspect-video justify-self-center md:justify-self-end object-cover object-center rounded-3xl ring-2 ring-[#523122]/25 shadow-[0_20px_40px_-25px_rgba(82,49,34,0.45)]"
          >
            <track kind="captions" src="/captions/blank.vtt" srcLang="en" label="English" />
          </video>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-10">
          <video
            ref={restaurantsVideoRef}
            src="https://res.cloudinary.com/ddahd2kco/video/upload/v1770558317/restaurants_ijuuqh.mp4"
            loop
            muted
            playsInline
            preload="metadata"
            poster="/restaurant_sideimage.png"
            onLoadedMetadata={refreshScrollTriggers}
            aria-hidden="true"
            tabIndex={-1}
            className="order-2 md:order-none w-full max-w-[420px] aspect-video justify-self-center md:justify-self-start object-cover object-center rounded-3xl ring-2 ring-[#523122]/25 shadow-[0_20px_40px_-25px_rgba(82,49,34,0.45)]"
          >
            <track kind="captions" src="/captions/blank.vtt" srcLang="en" label="English" />
          </video>

          <div className="order-1 md:order-none flex flex-col items-start md:items-end gap-4 md:text-right md:border-r-2 md:border-[#523122]/25 md:pr-4">
            <h1 className="nutrition-title text-6xl md:text-7xl font-bold uppercase text-[#523122] leading-[0.9] tracking-tighter">
              FOR RESTAURANT OWNERS
            </h1>
            <div className="flex flex-col gap-2 items-start md:items-end">
              <p className="font-paragraph text-2xl md:text-3xl text-dark-brown leading-tight max-w-xl">
                <span className="text-[#523122] font-bold">Grow</span> your Restaurant Business.
              </p>
              <p className="font-paragraph text-xl md:text-2xl text-dark-brown/80">
                Our Investment, Your Returns
              </p>
            </div>

            <button onClick={() => triggerTransition("/restaurants")} className="nutrition-button mt-4">
              Unlock Potential
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NutritionSection;
